import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr, STAGES, STAGE_BY_ID } from "@/lib/api";
import { PageHeader, PrimaryButton, GhostButton, StageBadge } from "@/components/Primitives";
import RangeSelect, { DEFAULT_RANGE } from "@/components/RangeSelect";
import ExportButton from "@/components/ExportButton";
import Modal, { inputCls, Field } from "@/components/Modal";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { Plus, X, GripVertical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

const SOURCES = ["Website", "Facebook", "Instagram", "Referral", "Google", "Cold Outreach", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const LOSS_REASONS = ["Price", "Competitor", "No Budget", "No Response", "Timing", "Other"];

function PipelineCard({ item, onClick, onDragStart, onDragEnd, isDragging }) {
  const stage = STAGE_BY_ID[item.stage] || STAGES[0];
  return (
    <div
      data-testid={`pipeline-card-${item.id}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(item); }}
      onDragEnd={onDragEnd}
      onClick={() => onClick(item)}
      className={`group rounded-2xl p-4 cursor-grab active:cursor-grabbing transition-all border ${isDragging ? "kanban-dragging" : ""}`}
      style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }}
    >
      <div className={`h-0.5 w-10 rounded-full bg-gradient-to-r ${stage.accent} mb-3`} />
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{item.company_name}</div>
          <div className="text-xs text-white/45 truncate">{item.contact_name || "—"}</div>
        </div>
        <GripVertical className="w-3.5 h-3.5 text-white/20 opacity-0 group-hover:opacity-100 transition" />
      </div>
      {item.project_name && <div className="mt-2 text-[11px] text-white/55 truncate">{item.project_name}</div>}
      <div className="flex items-center justify-between mt-4">
        <div className="font-display text-lg font-light text-white">{fmtMoney(item.estimated_value)}</div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${
          item.priority === "Urgent" ? "bg-rose-400/10 text-rose-300 border-rose-400/20" :
          item.priority === "High" ? "bg-amber-400/10 text-amber-300 border-amber-400/20" :
          item.priority === "Medium" ? "bg-sky-400/10 text-sky-300 border-sky-400/20" :
          "bg-white/5 text-white/50 border-white/10"
        }`}>{item.priority}</span>
      </div>
      <div className="text-[10px] text-white/30 mt-3">Updated {fmtDate(item.updated_at)}</div>
    </div>
  );
}

function PipelineDialog({ open, initial, onClose, onSaved }) {
  const blank = {
    company_name: "", contact_name: "", contact_email: "", contact_phone: "",
    website: "", industry: "", lead_source: "Website",
    estimated_value: 0, priority: "Medium", project_name: "", notes: "",
    stage: "lead", next_follow_up_date: "", follow_up_notes: "", loss_reason: "",
    requirements: "", services_requested: "", budget: 0, timeline: "",
    proposal_amount: 0, proposal_date: "", expiration_date: "", proposal_status: "",
    deployment_date: "", production_url: "", created_at: "",
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...blank, ...initial,
          next_follow_up_date: (initial.next_follow_up_date || "").slice(0, 10),
          proposal_date: (initial.proposal_date || "").slice(0, 10),
          expiration_date: (initial.expiration_date || "").slice(0, 10),
          deployment_date: (initial.deployment_date || "").slice(0, 10),
          created_at: (initial.created_at || "").slice(0, 16),
          services_requested: Array.isArray(initial.services_requested) ? initial.services_requested.join(", ") : (initial.services_requested || ""),
        });
      } else setForm(blank);
    }
  }, [open, initial]); // eslint-disable-line

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!initial?.id;

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = {
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        website: form.website,
        industry: form.industry,
        lead_source: form.lead_source,
        priority: form.priority,
        estimated_value: Number(form.estimated_value) || 0,
        project_name: form.project_name,
        notes: form.notes,
        stage: form.stage,
        next_follow_up_date: form.next_follow_up_date || null,
        follow_up_notes: form.follow_up_notes,
        loss_reason: form.loss_reason || null,
        requirements: form.requirements,
        services_requested: typeof form.services_requested === "string" && form.services_requested ? form.services_requested.split(",").map((s) => s.trim()) : (form.services_requested || []),
        budget: Number(form.budget) || 0,
        timeline: form.timeline,
        proposal_amount: Number(form.proposal_amount) || 0,
        proposal_date: form.proposal_date || null,
        expiration_date: form.expiration_date || null,
        proposal_status: form.proposal_status,
        deployment_date: form.deployment_date || null,
        production_url: form.production_url,
      };
      // Backdate (only on edit; new leads use server time)
      if (isEdit && form.created_at) {
        payload.created_at = new Date(form.created_at).toISOString();
      }
      if (isEdit) await api.patch(`/pipeline/${initial.id}`, payload);
      else await api.post("/pipeline", payload);
      toast.success(isEdit ? "Saved" : "Lead created");
      onClose(); onSaved();
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const confirm = useConfirm();
  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this lead?",
      description: `“${initial?.company_name || "This lead"}” will be removed from the pipeline.`,
      confirmLabel: "Delete lead",
    });
    if (!ok) return;
    try { await api.delete(`/pipeline/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <form onSubmit={save} className="p-8" data-testid="pipeline-dialog">
        <div className="flex items-start justify-between mb-6">
          <div>
            {isEdit && <StageBadge stage={form.stage} label={STAGE_BY_ID[form.stage]?.label} />}
            <h2 className="font-display text-2xl font-light mt-3">{isEdit ? form.company_name : "New lead"}</h2>
            {isEdit && <div className="text-white/45 text-sm">{form.contact_name}</div>}
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company Name"><input required className={inputCls} value={form.company_name} onChange={update("company_name")} data-testid="pl-company" /></Field>
          <Field label="Project Name"><input className={inputCls} value={form.project_name} onChange={update("project_name")} /></Field>
          <Field label="Contact Name"><input className={inputCls} value={form.contact_name} onChange={update("contact_name")} /></Field>
          <Field label="Email"><input className={inputCls} value={form.contact_email} onChange={update("contact_email")} /></Field>
          <Field label="Phone"><input className={inputCls} value={form.contact_phone} onChange={update("contact_phone")} /></Field>
          <Field label="Website"><input className={inputCls} value={form.website} onChange={update("website")} /></Field>
          <Field label="Industry"><input className={inputCls} value={form.industry} onChange={update("industry")} /></Field>
          <Field label="Lead Source">
            <select className={inputCls} value={form.lead_source} onChange={update("lead_source")}>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Estimated Value"><input type="number" className={inputCls} value={form.estimated_value} onChange={update("estimated_value")} /></Field>
          <Field label="Priority">
            <select className={inputCls} value={form.priority} onChange={update("priority")}>
              {PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Stage">
            <select className={inputCls} value={form.stage} onChange={update("stage")} data-testid="pl-stage">
              {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          {isEdit && (
            <Field label="Lead Created (backdate)">
              <input type="datetime-local" className={inputCls} value={form.created_at} onChange={update("created_at")} data-testid="pl-created-at" />
            </Field>
          )}
          {form.stage === "follow_up" && (
            <>
              <Field label="Next Follow-up"><input type="date" className={inputCls} value={form.next_follow_up_date} onChange={update("next_follow_up_date")} /></Field>
              <Field label="Follow-up Notes"><input className={inputCls} value={form.follow_up_notes} onChange={update("follow_up_notes")} /></Field>
            </>
          )}
          {form.stage === "proposal" && (
            <>
              <Field label="Proposal Amount"><input type="number" className={inputCls} value={form.proposal_amount} onChange={update("proposal_amount")} /></Field>
              <Field label="Proposal Date"><input type="date" className={inputCls} value={form.proposal_date} onChange={update("proposal_date")} /></Field>
              <Field label="Expiration"><input type="date" className={inputCls} value={form.expiration_date} onChange={update("expiration_date")} /></Field>
              <Field label="Proposal Status"><input className={inputCls} value={form.proposal_status} onChange={update("proposal_status")} /></Field>
            </>
          )}
          {form.stage === "deployed" && (
            <>
              <Field label="Deployment Date"><input type="date" className={inputCls} value={form.deployment_date} onChange={update("deployment_date")} /></Field>
              <Field label="Production URL"><input className={inputCls} value={form.production_url} onChange={update("production_url")} /></Field>
            </>
          )}
          {form.stage === "lost" && (
            <Field label="Loss Reason">
              <select className={inputCls} value={form.loss_reason} onChange={update("loss_reason")} data-testid="pl-loss-reason">
                <option value="">Select…</option>
                {LOSS_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
          <div className="sm:col-span-2">
            <Field label="Notes"><textarea rows={3} className={inputCls} value={form.notes} onChange={update("notes")} /></Field>
          </div>
        </div>

        {isEdit && (
          <div className="mt-8 pt-6 border-t border-white/[0.06]">
            <AttachmentsPanel parentType="pipeline" parentId={initial.id} testIdPrefix="pipeline-att" />
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 hover:text-rose-300 text-sm inline-flex items-center gap-1.5" data-testid="pl-delete"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit" disabled={busy} data-testid="pl-save">{busy ? "Saving…" : (isEdit ? "Save" : "Create Lead")}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default function Pipeline() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const load = async () => {
    const { data } = await api.get("/pipeline");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open new lead dialog when arriving via Quick Actions
  useEffect(() => {
    if (sessionStorage.getItem("equinox_quick_action") === "lead") {
      sessionStorage.removeItem("equinox_quick_action");
      setActive(null); setOpen(true);
    }
  }, []);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    items.forEach((it) => { if (m[it.stage]) m[it.stage].push(it); });
    return m;
  }, [items]);

  const moveTo = async (item, stageId) => {
    if (!item || item.stage === stageId) return;
    if (stageId === "lost" && !item.loss_reason) {
      const reason = window.prompt(`Loss reason (${LOSS_REASONS.join(", ")}):`, "Other");
      if (!reason) return;
      try { await api.patch(`/pipeline/${item.id}`, { stage: stageId, loss_reason: reason }); toast.success("Moved to Lost"); }
      catch (e) { toast.error(formatErr(e)); return; }
    } else {
      try { await api.patch(`/pipeline/${item.id}`, { stage: stageId }); toast.success(`Moved to ${STAGE_BY_ID[stageId].label}`); }
      catch (e) { toast.error(formatErr(e)); return; }
    }
    load();
  };

  return (
    <div data-testid="pipeline-page" className="flex flex-col" style={{ height: "calc(100vh - 130px)" }}>
      <PageHeader
        title="Pipeline"
        subtitle="Drag deals through the lifecycle — from first touch to deployed."
        testId="pipeline-header"
        action={
          <div className="flex items-center gap-2.5">
            <RangeSelect value={range} onChange={setRange} testIdPrefix="pipeline-range" />
            <ExportButton endpoint="/exports/pipeline" range={range} testId="pipeline-export-btn" label="Export" />
            <PrimaryButton onClick={() => { setActive(null); setOpen(true); }} data-testid="new-lead-btn">
              <Plus className="w-4 h-4" /> New Lead
            </PrimaryButton>
          </div>
        }
      />

      <div className="flex-1 overflow-x-auto overflow-y-hidden pb-3" data-testid="kanban-board">
        <div className="flex gap-5 h-full pr-4">
          {STAGES.map((stage) => {
            const list = byStage[stage.id] || [];
            const total = list.reduce((s, it) => s + (it.estimated_value || 0), 0);
            return (
              <div
                key={stage.id}
                className={`w-[300px] flex-shrink-0 flex flex-col gap-3 rounded-3xl p-3 border transition ${overStage === stage.id ? "kanban-over" : "border-transparent"}`}
                onDragOver={(e) => { e.preventDefault(); setOverStage(stage.id); }}
                onDragLeave={() => setOverStage(null)}
                onDrop={(e) => {
                  e.preventDefault(); setOverStage(null);
                  const item = items.find((i) => i.id === dragId);
                  moveTo(item, stage.id); setDragId(null);
                }}
                data-testid={`kanban-col-${stage.id}`}
              >
                <div className="px-2 pt-2 pb-1 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${stage.accent}`} />
                      <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-medium">{stage.label}</div>
                    </div>
                    <div className="text-[10px] text-white/40">{list.length}</div>
                  </div>
                  <div className="text-[10px] text-white/30 mt-1">{fmtMoney(total)}</div>
                </div>
                <div className="flex flex-col gap-3 overflow-y-auto pr-1 flex-1">
                  {list.map((it) => (
                    <PipelineCard
                      key={it.id} item={it}
                      isDragging={dragId === it.id}
                      onDragStart={(item) => setDragId(item.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={(it2) => { setActive(it2); setOpen(true); }}
                    />
                  ))}
                  {list.length === 0 && (
                    <div className="text-[10px] text-white/25 text-center py-6 border border-dashed border-white/5 rounded-2xl">Drop here</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PipelineDialog open={open} initial={active} onClose={() => { setOpen(false); setActive(null); }} onSaved={load} />
    </div>
  );
}
